import { EnergyBackupSystem, EquipmentType, SearchProductDTO } from 'types';

/**
 * Represents a single model number selection for one unit of equipment.
 * This allows tracking per-unit selections without mutating the equipment array.
 */
export interface ModelSelection {
  /** Index of the equipment item in the original array */
  equipmentIndex: number;
  /** Index of the model within the equipment item */
  modelIndex: number;
  /** Which unit this is (0-indexed) when quantity > 1 */
  unitIndex: number;
  /** The selected model number (SKU) for this unit */
  modelNumber?: string;
  /** Equipment type for display */
  type: EquipmentType;
  /** Manufacturer for display */
  manufacturer: string;
  /** Model name for display */
  modelName: string;
}

/**
 * Builds a flat array of model selections from equipment.
 * Each unit of equipment (based on quantity) gets its own selection entry.
 *
 * Example:
 * Input: [{ manufacturer: "Tesla", models: [{ name: "Powerwall 3", quantity: 2 }] }]
 * Output: [
 *   { equipmentIndex: 0, modelIndex: 0, unitIndex: 0, modelName: "Powerwall 3", ... },
 *   { equipmentIndex: 0, modelIndex: 0, unitIndex: 1, modelName: "Powerwall 3", ... }
 * ]
 */
export const buildModelSelections = (equipment: EnergyBackupSystem[]): ModelSelection[] => {
  const selections: ModelSelection[] = [];

  equipment.forEach((item, eqIdx) => {
    item.models.forEach((model, modelIdx) => {
      for (let unitIdx = 0; unitIdx < model.quantity; unitIdx++) {
        selections.push({
          equipmentIndex: eqIdx,
          modelIndex: modelIdx,
          unitIndex: unitIdx,
          modelNumber: model.modelNumber,
          type: item.type,
          manufacturer: item.manufacturer,
          modelName: model.name,
        });
      }
    });
  });

  return selections;
};

/**
 * Applies model selections back to equipment, splitting items only when
 * users selected different modelNumbers for units that originally had quantity > 1.
 *
 * Example 1 - Same modelNumber (no split):
 * Equipment: [{ models: [{ name: "Powerwall 3", quantity: 2 }] }]
 * Selections: [{ modelNumber: "A" }, { modelNumber: "A" }]
 * Result: [{ models: [{ name: "Powerwall 3", quantity: 2, modelNumber: "A" }] }]
 *
 * Example 2 - Different modelNumbers (splits):
 * Equipment: [{ models: [{ name: "Powerwall 3", quantity: 2 }] }]
 * Selections: [{ modelNumber: "A" }, { modelNumber: "B" }]
 * Result: [
 *   { models: [{ name: "Powerwall 3", quantity: 1, modelNumber: "A" }] },
 *   { models: [{ name: "Powerwall 3", quantity: 1, modelNumber: "B" }] }
 * ]
 */
export const applyModelSelections = (
  equipment: EnergyBackupSystem[],
  selections: ModelSelection[],
): EnergyBackupSystem[] => {
  if (!equipment?.length || !selections?.length) {
    return equipment;
  }

  // Group selections by their equipment/model position
  const selectionsByPosition = new Map<string, ModelSelection[]>();

  for (const sel of selections) {
    const key = `${sel.equipmentIndex}|${sel.modelIndex}`;
    if (!selectionsByPosition.has(key)) {
      selectionsByPosition.set(key, []);
    }
    selectionsByPosition.get(key)!.push(sel);
  }

  const result: EnergyBackupSystem[] = [];

  equipment.forEach((item, eqIdx) => {
    // Track if any model in this item needs splitting
    const splitItems: EnergyBackupSystem[] = [];
    const combinedModels: typeof item.models = [];
    let needsSplit = false;

    item.models.forEach((model, modelIdx) => {
      const key = `${eqIdx}|${modelIdx}`;
      const positionSelections = selectionsByPosition.get(key) || [];

      if (positionSelections.length === 0) {
        // No selections for this model, keep original
        combinedModels.push({ ...model });
        return;
      }

      // Group selections by modelNumber
      const byModelNumber = new Map<string, ModelSelection[]>();
      for (const sel of positionSelections) {
        const mn = sel.modelNumber || '';
        if (!byModelNumber.has(mn)) {
          byModelNumber.set(mn, []);
        }
        byModelNumber.get(mn)!.push(sel);
      }

      // Check if all selections have the same modelNumber
      const uniqueModelNumbers = Array.from(byModelNumber.keys()).filter(
        (mn) => mn !== '' || byModelNumber.size === 1,
      );

      if (model.quantity > 1 && uniqueModelNumbers.length > 1) {
        // Different modelNumbers for a quantity > 1 item - need to split
        needsSplit = true;

        let isFirst = true;
        for (const [modelNumber, sels] of byModelNumber) {
          splitItems.push({
            ...item,
            models: [
              {
                ...model,
                quantity: sels.length,
                modelNumber: modelNumber || model.modelNumber,
              },
            ],
            totalCost: isFirst ? item.totalCost : 0,
          });
          isFirst = false;
        }
      } else {
        // All same modelNumber - keep combined
        const modelNumber = positionSelections[0]?.modelNumber || model.modelNumber;
        combinedModels.push({
          ...model,
          modelNumber,
        });
      }
    });

    if (needsSplit) {
      result.push(...splitItems);
    } else {
      result.push({
        ...item,
        models: combinedModels,
      });
    }
  });

  return result;
};

/**
 * Some existing system designs may have a model name that no longer matches any current
 * `product-line` value exactly (e.g. it was saved with a SKU in the name), which would
 * otherwise zero out the results even though the correct product is present in the response.
 * Falling back to every result for the equipment's manufacturer keeps the dropdown usable
 * for those accounts instead of silently returning nothing.
 */
export const filterProductsForModelSelection = (
  products: SearchProductDTO[],
  manufacturer: string,
  modelName: string,
): SearchProductDTO[] => {
  const manufacturerMatches = products.filter(
    (product) => product.manufacturer.toLowerCase() === manufacturer.toLowerCase(),
  );

  const exactProductLineMatches = manufacturerMatches.filter((product) => {
    const productLine = product.attributes.find((attr: Record<string, any>) => attr.key === 'product-line')
      ?.value as string;
    return productLine?.toLowerCase() === modelName.toLowerCase();
  });

  return exactProductLineMatches.length ? exactProductLineMatches : manufacturerMatches;
};
